import { supabase } from '../lib/supabase';

class PaymentService {
  // Create a new transaction record (requires seller approval)
  async createTransaction(transactionData) {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .insert({
          listing_id: transactionData.listingId,
          buyer_id: transactionData.buyerId,
          seller_id: transactionData.sellerId,
          amount_cents: transactionData.amountCents,
          payment_method: transactionData.paymentMethod,
          status: 'completed', // Use 'completed' as the default status
          approval_status: 'pending', // Track approval state separately
          notes: transactionData.notes || null
        })
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error creating transaction:', error);
      return { success: false, message: error.message };
    }
  }

  // Approve a payment (seller action)
  async approvePayment(transactionId, approvedBy) {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .update({
          approval_status: 'approved',
          approved_at: new Date().toISOString(),
          approved_by: approvedBy
        })
        .eq('id', transactionId)
        .select()
        .single();

      if (error) throw error;

      // Automatically mark the listing as sold
      const { error: listingError } = await supabase
        .from('listings')
        .update({ status: 'sold' })
        .eq('id', data.listing_id);

      if (listingError) {
        console.error('Error marking listing as sold:', listingError);
        // Don't fail the approval if this fails
      }

      // Create a purchase record for history tracking
      const { error: purchaseError } = await supabase
        .from('purchases')
        .insert({
          listing_id: data.listing_id,
          buyer_id: data.buyer_id,
          seller_id: data.seller_id,
          purchase_date: new Date().toISOString(),
          amount_cents: data.amount_cents,
          payment_method: data.payment_method
        });

      if (purchaseError) {
        console.error('Error creating purchase record:', purchaseError);
        // Don't fail the approval if this fails
      }

      // Create notification for buyer
      await supabase
        .from('notifications')
        .insert([{
          user_id: data.buyer_id,
          type: 'payment_approved',
          title: 'Payment Approved',
          message: 'Your payment has been approved by the seller.',
          data: {
            transaction_id: transactionId,
            listing_id: data.listing_id
          }
        }]);

      return { success: true, data };
    } catch (error) {
      console.error('Error approving payment:', error);
      return { success: false, message: error.message };
    }
  }

  // Reject a payment (seller action)
  async rejectPayment(transactionId, rejectionReason, rejectedBy) {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .update({
          approval_status: 'rejected',
          approved_at: new Date().toISOString(),
          approved_by: rejectedBy,
          rejection_reason: rejectionReason
        })
        .eq('id', transactionId)
        .select()
        .single();

      if (error) throw error;

      // Create notification for buyer
      await supabase
        .from('notifications')
        .insert([{
          user_id: data.buyer_id,
          type: 'payment_rejected',
          title: 'Payment Rejected',
          message: `Your payment was rejected. Reason: ${rejectionReason}`,
          data: {
            transaction_id: transactionId,
            listing_id: data.listing_id,
            rejection_reason: rejectionReason
          }
        }]);

      return { success: true, data };
    } catch (error) {
      console.error('Error rejecting payment:', error);
      return { success: false, message: error.message };
    }
  }

  // Get pending payment approvals for a seller
  async getPendingApprovals(sellerId) {
    try {
      // First, get the transactions
      const { data: transactions, error: transactionsError } = await supabase
        .from('transactions')
        .select('*')
        .eq('seller_id', sellerId)
        .eq('approval_status', 'pending')
        .order('created_at', { ascending: false });

      if (transactionsError) throw transactionsError;

      if (!transactions || transactions.length === 0) {
        return { success: true, data: [] };
      }

      // Get listing IDs and buyer IDs
      const listingIds = [...new Set(transactions.map(t => t.listing_id))];
      const buyerIds = [...new Set(transactions.map(t => t.buyer_id))];

      // Fetch listings
      const { data: listings, error: listingsError } = await supabase
        .from('listings')
        .select('id, title, photos, category')
        .in('id', listingIds);

      if (listingsError) throw listingsError;

      // Fetch buyer profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, name')
        .in('id', buyerIds);

      if (profilesError) throw profilesError;

      // Create lookup maps
      const listingsMap = {};
      listings.forEach(listing => {
        listingsMap[listing.id] = listing;
      });

      const profilesMap = {};
      profiles.forEach(profile => {
        profilesMap[profile.id] = profile;
      });

      // Combine the data
      const enrichedTransactions = transactions.map(transaction => ({
        ...transaction,
        listings: listingsMap[transaction.listing_id] || null,
        buyer: profilesMap[transaction.buyer_id] || { id: transaction.buyer_id, name: 'Unknown' }
      }));

      return { success: true, data: enrichedTransactions };
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      return { success: false, message: error.message };
    }
  }

  // Get transaction history for a user (as buyer or seller)
  async getUserTransactions(userId, type = 'all') {
    try {
      let query = supabase
        .from('transactions')
        .select(`
          *,
          listings (
            id,
            title,
            photos,
            category
          ),
          buyer:buyer_id (
            id,
            name
          ),
          seller:seller_id (
            id,
            name
          )
        `);

      if (type === 'bought') {
        query = query.eq('buyer_id', userId);
      } else if (type === 'sold') {
        query = query.eq('seller_id', userId);
      } else {
        query = query.or(`buyer_id.eq.${userId},seller_id.eq.${userId}`);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching user transactions:', error);
      return { success: false, message: error.message };
    }
  }

  // Get transaction details by ID
  async getTransaction(transactionId) {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select(`
          *,
          listings (
            id,
            title,
            description,
            photos,
            category,
            condition
          ),
          buyer:buyer_id (
            id,
            name,
            avatar_url
          ),
          seller:seller_id (
            id,
            name,
            avatar_url
          )
        `)
        .eq('id', transactionId)
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error fetching transaction:', error);
      return { success: false, message: error.message };
    }
  }

  // Update transaction status (for disputes, refunds, etc.)
  async updateTransactionStatus(transactionId, status, notes = null) {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .update({ 
          status,
          notes: notes || null
        })
        .eq('id', transactionId)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error updating transaction:', error);
      return { success: false, message: error.message };
    }
  }

  // Get transaction statistics for a user
  async getUserTransactionStats(userId) {
    try {
      // Get transactions as buyer
      const { data: boughtTransactions, error: boughtError } = await supabase
        .from('transactions')
        .select('amount_cents')
        .eq('buyer_id', userId)
        .eq('status', 'completed');

      if (boughtError) throw boughtError;

      // Get transactions as seller
      const { data: soldTransactions, error: soldError } = await supabase
        .from('transactions')
        .select('amount_cents')
        .eq('seller_id', userId)
        .eq('status', 'completed');

      if (soldError) throw soldError;

      const stats = {
        totalBought: boughtTransactions?.length || 0,
        totalSold: soldTransactions?.length || 0,
        totalSpent: boughtTransactions?.reduce((sum, t) => sum + t.amount_cents, 0) || 0,
        totalEarned: soldTransactions?.reduce((sum, t) => sum + t.amount_cents, 0) || 0
      };

      return { success: true, data: stats };
    } catch (error) {
      console.error('Error fetching transaction stats:', error);
      return { success: false, message: error.message };
    }
  }

  // Check if a listing has been sold (has transactions)
  async getListingTransactions(listingId) {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select(`
          *,
          buyer:buyer_id (
            id,
            name
          )
        `)
        .eq('listing_id', listingId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching listing transactions:', error);
      return { success: false, message: error.message };
    }
  }

  // Get payment method options
  getPaymentMethods() {
    return [
      { value: 'cash', label: 'Cash', icon: '💵', description: 'Pay with cash when meeting' },
      { value: 'interac', label: 'Interac e-Transfer', icon: '🏦', description: 'Send money via email/phone' },
      { value: 'paypal', label: 'PayPal', icon: '💳', description: 'PayPal Friends & Family' },
      { value: 'venmo', label: 'Venmo', icon: '📱', description: 'Venmo transfer' },
      { value: 'zelle', label: 'Zelle', icon: '⚡', description: 'Zelle instant transfer' },
      { value: 'cash_app', label: 'Cash App', icon: '💰', description: 'Cash App transfer' },
      { value: 'other', label: 'Other', icon: '🔄', description: 'Other payment method' }
    ];
  }

  // Format amount for display
  formatAmount(amountCents) {
    return `$${(amountCents / 100).toFixed(2)}`;
  }

  // Parse amount from string to cents
  parseAmount(amountString) {
    const cleanAmount = amountString.replace(/[^0-9.]/g, '');
    return Math.round(parseFloat(cleanAmount) * 100);
  }
}

export const paymentService = new PaymentService();